import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ChevronRight, 
  ChevronLeft, 
  ExternalLink, 
  Copy, 
  Check,
  Key,
  Settings,
  Link2,
  Shield,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Setup instructions for each CMS
const API_URL = process.env.REACT_APP_BACKEND_URL;

const CMS_SETUP_GUIDES = {
  filevine: {
    name: "Filevine",
    steps: [
      {
        title: "Access API Settings",
        description: "Log in to your Filevine account and navigate to Settings → API & Integrations.",
        tip: "You'll need admin access to view API settings."
      },
      {
        title: "Create API Application",
        description: "Click 'Create New Application' and enter:\n• Name: IntakePal\n• Description: Intake automation integration\n• Redirect URI: Copy from below",
        copyValue: `${API_URL}/integrations/cms/callback/filevine`
      },
      {
        title: "Get Credentials",
        description: "After creating the app, you'll receive:\n• Client ID\n• Client Secret\n\nKeep these safe - you'll need them to connect.",
        tip: "Never share your Client Secret publicly."
      },
      {
        title: "Connect to IntakePal",
        description: "Return to IntakePal and click 'Connect' on the Filevine card. You'll be redirected to authorize the connection.",
        tip: "Make sure pop-ups are enabled in your browser."
      }
    ],
    docsUrl: "https://developer.filevine.io/docs/v2-us",
    authType: "oauth",
    videoUrl: null
  },
  clio: {
    name: "Clio",
    steps: [
      {
        title: "Access Developer Portal",
        description: "Go to app.clio.com, click your profile icon → Settings → API.",
        tip: "You need to be a firm admin to access API settings."
      },
      {
        title: "Register Application",
        description: "Click 'Register New Application' and fill in:\n• Name: IntakePal\n• Website: https://intakepal.com\n• Redirect URI: Copy from below",
        copyValue: `${API_URL}/integrations/cms/callback/clio`
      },
      {
        title: "Select Permissions",
        description: "Enable these permissions:\n• Read/Write Contacts\n• Read/Write Matters\n• Read/Write Documents\n• Read/Write Notes",
        tip: "IntakePal needs these to create cases and sync client info."
      },
      {
        title: "Copy Credentials",
        description: "After registration, copy your:\n• Client ID\n• Client Secret\n\nStore these securely.",
      },
      {
        title: "Connect to IntakePal",
        description: "Return here, click 'Connect', and authorize Clio to share data with IntakePal.",
      }
    ],
    docsUrl: "https://docs.developers.clio.com",
    authType: "oauth"
  },
  practicepanther: {
    name: "PracticePanther",
    steps: [
      {
        title: "Request API Access",
        description: "PracticePanther requires approval for API access. Contact their support team to request API credentials.",
        tip: "Mention you're integrating with IntakePal for intake automation."
      },
      {
        title: "Create OAuth App",
        description: "Once approved, go to Settings → Integrations → API and create a new OAuth application.",
        copyValue: `${API_URL}/integrations/cms/callback/practicepanther`
      },
      {
        title: "Get Credentials",
        description: "You'll receive:\n• Client ID\n• Client Secret\n\nKeep these credentials secure."
      },
      {
        title: "Connect",
        description: "Return to IntakePal and click 'Connect' to authorize the integration."
      }
    ],
    docsUrl: "https://www.practicepanther.com/api-docs",
    authType: "oauth"
  },
  mycase: {
    name: "MyCase",
    steps: [
      {
        title: "Access API Settings",
        description: "Log in to MyCase and go to Settings → Integrations → API Access.",
        tip: "Only firm administrators can access API settings."
      },
      {
        title: "Generate API Key",
        description: "Click 'Generate New API Key' and give it a name like 'IntakePal Integration'.",
        tip: "You can create multiple keys for different integrations."
      },
      {
        title: "Copy Your API Key",
        description: "Copy the generated API key immediately - it won't be shown again!\n\nThe key looks like: mc_live_xxxxxxxxxxxx",
        tip: "Store this key securely. If lost, you'll need to generate a new one."
      },
      {
        title: "Enter Key in IntakePal",
        description: "Paste your API key in the connection dialog and click 'Connect'."
      }
    ],
    docsUrl: "https://www.mycase.com/api-documentation",
    authType: "api_key"
  },
  smokeball: {
    name: "Smokeball",
    steps: [
      {
        title: "Contact Smokeball",
        description: "Smokeball API access requires partnership approval. Email their integrations team at integrations@smokeball.com",
        tip: "Mention you're connecting IntakePal for automated intake."
      },
      {
        title: "Receive Credentials",
        description: "After approval, Smokeball will provide:\n• API Key\n• API Secret (if applicable)\n• Your firm's API endpoint"
      },
      {
        title: "Enter Credentials",
        description: "Enter your Smokeball API key in the IntakePal connection dialog."
      }
    ],
    docsUrl: "https://developer.smokeball.com",
    authType: "api_key"
  },
  litify: {
    name: "Litify",
    steps: [
      {
        title: "Access Salesforce Setup",
        description: "Litify runs on Salesforce. Go to Setup → Apps → App Manager in your Salesforce org.",
        tip: "You need Salesforce admin permissions."
      },
      {
        title: "Create Connected App",
        description: "Click 'New Connected App' and configure:\n• App Name: IntakePal\n• Enable OAuth Settings\n• Callback URL: Copy from below\n• Scopes: Full access, API",
        copyValue: `${API_URL}/integrations/cms/callback/litify`
      },
      {
        title: "Get Consumer Details",
        description: "After saving, click 'Manage Consumer Details' to get:\n• Consumer Key (Client ID)\n• Consumer Secret"
      },
      {
        title: "Note Your Instance URL",
        description: "Your Salesforce instance URL (e.g., yourfirm.lightning.force.com) is needed for the connection."
      },
      {
        title: "Connect",
        description: "Return to IntakePal and complete the OAuth flow to connect Litify."
      }
    ],
    docsUrl: "https://developer.salesforce.com/docs",
    authType: "oauth"
  },
  casepeer: {
    name: "CASEpeer",
    steps: [
      {
        title: "Access API Settings",
        description: "Log in to CASEpeer and navigate to Settings → Integrations → API.",
        tip: "Admin access is required for API configuration."
      },
      {
        title: "Generate API Key",
        description: "Click 'Generate API Key' and label it 'IntakePal'.",
        tip: "CASEpeer is now part of Litify - interface may vary."
      },
      {
        title: "Copy API Key",
        description: "Copy your API key. It typically starts with 'cp_' followed by a long string.",
        tip: "Save this immediately - you may not see it again."
      },
      {
        title: "Enter in IntakePal",
        description: "Paste your CASEpeer API key in the connection dialog and click 'Connect'."
      }
    ],
    docsUrl: "https://casepeer.com/integrations",
    authType: "api_key"
  },
  smartadvocate: {
    name: "SmartAdvocate",
    steps: [
      {
        title: "Access Admin Panel",
        description: "Log in to SmartAdvocate with admin credentials and go to Admin → System → API Settings.",
        tip: "Only system administrators can access API settings."
      },
      {
        title: "Enable API Access",
        description: "Toggle 'Enable API Access' if not already enabled, then click 'Generate New API Key'."
      },
      {
        title: "Copy Credentials",
        description: "Copy both:\n• API Key\n• API Secret\n\nYou'll need both to connect."
      },
      {
        title: "Configure Permissions",
        description: "Set the following permissions for the key:\n• Cases: Read/Write\n• Contacts: Read/Write\n• Documents: Write"
      },
      {
        title: "Enter in IntakePal",
        description: "Paste your SmartAdvocate API credentials and connect."
      }
    ],
    docsUrl: "https://www.smartadvocate.com/api",
    authType: "api_key"
  },
  lawmatics: {
    name: "Lawmatics",
    steps: [
      {
        title: "Access Integrations",
        description: "Log in to Lawmatics and go to Settings → Integrations → API.",
        tip: "Lawmatics is a legal CRM focused on intake and marketing."
      },
      {
        title: "Generate API Key",
        description: "Click 'Create API Key', name it 'IntakePal', and set appropriate permissions."
      },
      {
        title: "Copy API Key",
        description: "Copy your new API key. It's a long alphanumeric string."
      },
      {
        title: "Connect",
        description: "Enter your Lawmatics API key in IntakePal to complete the connection."
      }
    ],
    docsUrl: "https://lawmatics.com/integrations",
    authType: "api_key"
  },
  neos: {
    name: "Neos (Needles)",
    steps: [
      {
        title: "Contact Assembly Software",
        description: "Neos API access requires approval. Contact Assembly Software support to request API credentials.",
        tip: "Neos was formerly known as Needles."
      },
      {
        title: "Receive API Credentials",
        description: "After approval, you'll receive:\n• API Key\n• API Endpoint URL\n• Documentation access"
      },
      {
        title: "Configure in IntakePal",
        description: "Enter your Neos API key to establish the connection."
      }
    ],
    docsUrl: "https://www.assemblysoftware.com/neos",
    authType: "api_key"
  },
  cloudlex: {
    name: "CloudLex",
    steps: [
      {
        title: "Access Developer Settings",
        description: "Log in to CloudLex and navigate to Settings → API & Integrations.",
        tip: "CloudLex is built specifically for personal injury firms."
      },
      {
        title: "Register Application",
        description: "Click 'Add Integration' and enter:\n• Name: IntakePal\n• Redirect URI: Copy from below",
        copyValue: `${API_URL}/integrations/cms/callback/cloudlex`
      },
      {
        title: "Get OAuth Credentials",
        description: "CloudLex will provide:\n• Client ID\n• Client Secret"
      },
      {
        title: "Authorize Connection",
        description: "Click 'Connect' in IntakePal to complete the OAuth authorization."
      }
    ],
    docsUrl: "https://cloudlex.com/integrations",
    authType: "oauth"
  },
  prevail: {
    name: "Prevail",
    steps: [
      {
        title: "Create Zapier Account",
        description: "Prevail integrates via Zapier. If you don't have one, create a free account at zapier.com",
        tip: "Zapier connects Prevail to thousands of other apps."
      },
      {
        title: "Create a New Zap",
        description: "In Zapier, click 'Create Zap' and select 'Webhooks by Zapier' as the trigger.",
        tip: "Choose 'Catch Hook' as the trigger event."
      },
      {
        title: "Copy Webhook URL",
        description: "Zapier will generate a unique webhook URL like:\nhttps://hooks.zapier.com/hooks/catch/xxxxx/xxxxx\n\nCopy this URL."
      },
      {
        title: "Set Up Prevail Action",
        description: "For the action step, search for 'Prevail' and select 'Create Case' or your preferred action."
      },
      {
        title: "Map Fields",
        description: "Map the incoming IntakePal data to Prevail fields:\n• Client Name → Contact Name\n• Accident Type → Case Type\n• etc."
      },
      {
        title: "Enter Webhook in IntakePal",
        description: "Paste your Zapier webhook URL in the IntakePal connection dialog."
      }
    ],
    docsUrl: "https://zapier.com/apps/prevail/integrations",
    authType: "zapier"
  }
};

const CMSSetupWizard = ({ cms, isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [copiedValue, setCopiedValue] = useState(null);
  
  const guide = CMS_SETUP_GUIDES[cms?.id];
  
  if (!guide) return null;
  
  const steps = guide.steps;
  const totalSteps = steps.length;
  const step = steps[currentStep];
  
  const handleCopy = (value) => {
    navigator.clipboard.writeText(value);
    setCopiedValue(value);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedValue(null), 2000);
  };
  
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-teal-600" />
            Connect {guide.name}
          </DialogTitle>
          <DialogDescription>
            Step {currentStep + 1} of {totalSteps}
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress Bar */}
        <div className="w-full bg-slate-100 rounded-full h-2 mb-4">
          <div 
            className="bg-teal-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>
        
        {/* Step Content */}
        <div className="py-4">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
              {currentStep === totalSteps - 1 ? (
                <CheckCircle className="w-5 h-5 text-teal-600" />
              ) : guide.authType === 'oauth' ? (
                <Link2 className="w-5 h-5 text-teal-600" />
              ) : guide.authType === 'zapier' ? (
                <Shield className="w-5 h-5 text-teal-600" />
              ) : (
                <Key className="w-5 h-5 text-teal-600" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-slate-600 text-sm whitespace-pre-line">{step.description}</p>
            </div>
          </div>
          
          {/* Copy Value Box */}
          {step.copyValue && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between gap-2">
                <code className="text-xs text-slate-700 break-all flex-1">
                  {step.copyValue}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopy(step.copyValue)}
                  className="flex-shrink-0"
                >
                  {copiedValue === step.copyValue ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {/* Tip Box */}
          {step.tip && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Tip:</strong> {step.tip}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <div>
            {guide.docsUrl && (
              <a
                href={guide.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
              >
                Official Docs
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handlePrev}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            <Button 
              onClick={handleNext}
              className="bg-teal-500 hover:bg-teal-600"
            >
              {currentStep === totalSteps - 1 ? (
                <>
                  Ready to Connect
                  <Check className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CMSSetupWizard;
export { CMS_SETUP_GUIDES };
